import { Plus, Trash2 } from "lucide-react";
import {
  DECISION_QUESTION_TYPES,
  decisionValueHandles,
  type WorkflowDecisionOption,
  type WorkflowDecisionQuestion,
} from "@proclaw/shared";
import { Field, Select, TextInput, Textarea, WorkflowToggle } from "./form-primitives";
import { ExpandableHighlightedTextarea } from "./expandable-highlighted-textarea";

type Question = WorkflowDecisionQuestion & Record<string, unknown>;

const QUESTION_TYPE_LABELS: Record<(typeof DECISION_QUESTION_TYPES)[number], string> = {
  choice: "Pick one option",
  noul: "Yes / no",
  score: "Score on a scale",
};

function slug(prefix: string, n: number): string {
  return `${prefix}${n}`;
}

function NumberInput({
  value,
  onChange,
  placeholder,
  readOnly,
  min,
  max,
  step,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
  readOnly?: boolean;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <input
      type="number"
      value={value ?? ""}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      readOnly={readOnly}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "") return onChange(undefined);
        const n = Number(raw);
        onChange(Number.isFinite(n) ? n : undefined);
      }}
      className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-text-primary outline-none transition-colors focus:border-accent read-only:cursor-not-allowed read-only:opacity-60"
    />
  );
}

/**
 * Config rail for a Decision node. Everything the runner reads lives in
 * node.data: state (templated inputs), questions, route and timeout. The
 * option values double as the node's outgoing handles, so editing them here
 * changes the connectors on the canvas (the canvas prunes edges whose handle
 * disappears).
 */
export function DecisionConfig({
  data,
  onChange,
  readOnly,
  variables,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
  variables?: { value: string; label: string; hint?: string }[];
}) {
  const update = (patch: Record<string, unknown>) => onChange({ ...data, ...patch });

  const state =
    data.state && typeof data.state === "object" && !Array.isArray(data.state)
      ? (data.state as Record<string, string>)
      : {};
  const stateEntries = Object.entries(state);
  const questions: Question[] = Array.isArray(data.questions) ? (data.questions as Question[]) : [];
  const route =
    data.route && typeof data.route === "object" ? (data.route as Record<string, unknown>) : {};
  const routedId =
    typeof route.question === "string" && questions.some((q) => q.id === route.question)
      ? (route.question as string)
      : (questions[0]?.id ?? "");
  const routed = questions.find((q) => q.id === routedId);
  const valueHandles = decisionValueHandles(data);

  const setState = (entries: Array<[string, string]>) =>
    update({ state: Object.fromEntries(entries) });
  const setQuestions = (next: Question[]) => update({ questions: next });
  const patchQuestion = (index: number, patch: Partial<Question>) =>
    setQuestions(questions.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  const setRoute = (patch: Record<string, unknown>) => update({ route: { ...route, ...patch } });

  const addQuestion = () => {
    if (questions.length >= 20) return;
    const used = new Set(questions.map((q) => q.id));
    let n = questions.length + 1;
    while (used.has(slug("q", n))) n += 1;
    setQuestions([
      ...questions,
      {
        id: slug("q", n),
        type: "choice",
        instructions: "",
        includeNone: true,
        noneFloor: 0.5,
        options: [{ value: "option_a", description: "" }, { value: "option_b", description: "" }],
      } as Question,
    ]);
  };

  return (
    <div className="space-y-5">
      <p className="text-[11px] leading-relaxed text-text-muted">
        Sends the state below to a decision model (TypeSafe Jev) and branches on the
        answer. It decides, it never writes. Runs on the platform in about a second.
      </p>

      {/* ── State ─────────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
            State (what the decision looks at)
          </p>
          {!readOnly && (
            <button
              type="button"
              onClick={() => {
                let n = stateEntries.length + 1;
                while (state[slug("field", n)] !== undefined) n += 1;
                setState([...stateEntries, [slug("field", n), ""]]);
              }}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-accent transition-colors hover:bg-accent/10"
            >
              <Plus size={12} />
              Add field
            </button>
          )}
        </div>
        {stateEntries.length === 0 && (
          <p className="text-[11px] text-amber-600">
            Add at least one field, e.g. message = {"{{input.body}}"}.
          </p>
        )}
        {stateEntries.map(([key, value], index) => (
          <div key={index} className="space-y-1.5 rounded-lg border border-border bg-background p-2.5">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <TextInput
                  value={key}
                  onChange={(next) =>
                    setState(stateEntries.map((e, i) => (i === index ? [next, e[1]] : e)))
                  }
                  placeholder="field name"
                  readOnly={readOnly}
                  mono
                />
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setState(stateEntries.filter((_e, i) => i !== index))}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
                  aria-label="Remove field"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <ExpandableHighlightedTextarea
              value={value}
              onChange={(next) =>
                setState(stateEntries.map((e, i) => (i === index ? [e[0], next] : e)))
              }
              placeholder="{{input.body}} or {{steps.review.outputs.summary}}"
              readOnly={readOnly}
              rows={1}
              variables={variables}
              title={`State field "${key}"`}
              singleLine
              className="h-[58px] font-mono text-xs"
            />
          </div>
        ))}
        <p className="text-[11px] text-text-muted">
          Send only the fields the decision needs. Accuracy falls as unrelated text grows.
        </p>
      </section>

      {/* ── Questions ─────────────────────────────────────────────────── */}
      <section className="space-y-2.5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
            Questions
          </p>
          {!readOnly && (
            <button
              type="button"
              onClick={addQuestion}
              disabled={questions.length >= 20}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-accent transition-colors hover:bg-accent/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus size={12} />
              Add question
            </button>
          )}
        </div>
        {questions.length === 0 && (
          <p className="text-[11px] text-amber-600">Add at least one question.</p>
        )}
        {questions.map((q, index) => {
          const options: WorkflowDecisionOption[] = Array.isArray(q.options) ? q.options : [];
          const levels: string[] = Array.isArray(q.levels) ? q.levels : [];
          return (
            <div key={index} className="space-y-2 rounded-lg border border-border bg-background p-2.5">
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <TextInput
                    value={q.id ?? ""}
                    onChange={(next) => patchQuestion(index, { id: next })}
                    placeholder="question id"
                    readOnly={readOnly}
                    mono
                  />
                </div>
                <div className="w-[150px] shrink-0">
                  <Select
                    value={q.type ?? "choice"}
                    onChange={(next) => patchQuestion(index, { type: next as Question["type"] })}
                    options={DECISION_QUESTION_TYPES.map((t) => ({ value: t, label: QUESTION_TYPE_LABELS[t] }))}
                    disabled={readOnly}
                  />
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => setQuestions(questions.filter((_q, i) => i !== index))}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
                    aria-label="Remove question"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>

              <Field label="Instructions">
                <Textarea
                  value={q.instructions ?? ""}
                  onChange={(next) => patchQuestion(index, { instructions: next })}
                  placeholder={
                    q.type === "noul"
                      ? "Is the sender asking us to do something for them?"
                      : q.type === "score"
                        ? "How urgent is this message?"
                        : "Decide in this order: 1. … → option_a. 2. … → option_b."
                  }
                  readOnly={readOnly}
                  rows={3}
                />
              </Field>

              {q.type === "choice" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
                      Options (each is a branch)
                    </p>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() =>
                          patchQuestion(index, {
                            options: [...options, { value: slug("option_", options.length + 1), description: "" }],
                          })
                        }
                        className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-accent transition-colors hover:bg-accent/10"
                      >
                        <Plus size={12} />
                        Add option
                      </button>
                    )}
                  </div>
                  {options.map((o, oi) => (
                    <div key={oi} className="space-y-1.5 rounded-md border border-border/70 p-2">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <TextInput
                            value={o.value ?? ""}
                            onChange={(next) =>
                              patchQuestion(index, {
                                options: options.map((x, i) => (i === oi ? { ...x, value: next.replace(/[^A-Za-z0-9_-]/g, "_") } : x)),
                              })
                            }
                            placeholder="value (becomes a branch)"
                            readOnly={readOnly}
                            mono
                          />
                        </div>
                        {!readOnly && options.length > 2 && (
                          <button
                            type="button"
                            onClick={() =>
                              patchQuestion(index, { options: options.filter((_x, i) => i !== oi) })
                            }
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
                            aria-label="Remove option"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <Textarea
                        value={typeof o.description === "string" ? o.description : o.description ? JSON.stringify(o.description) : ""}
                        onChange={(next) =>
                          patchQuestion(index, {
                            options: options.map((x, i) => (i === oi ? { ...x, description: next } : x)),
                          })
                        }
                        placeholder="What this option means, with examples. The model matches on this, not on the value."
                        readOnly={readOnly}
                        rows={2}
                      />
                    </div>
                  ))}
                  <WorkflowToggle
                    label='Offer "none of these fits"'
                    description="Recommended. Taken only when its probability is at or above the floor below, so a diffuse answer cannot block a reasonable option."
                    checked={q.includeNone === true}
                    disabled={readOnly}
                    onChange={(checked) => patchQuestion(index, { includeNone: checked })}
                  />
                  {q.includeNone === true && (
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="None floor (0–1)">
                        <NumberInput
                          value={typeof q.noneFloor === "number" ? q.noneFloor : undefined}
                          onChange={(v) => patchQuestion(index, { noneFloor: v })}
                          placeholder="0.5"
                          readOnly={readOnly}
                          min={0}
                          max={1}
                          step={0.05}
                        />
                      </Field>
                      <Field label="None description">
                        <TextInput
                          value={q.noneDescription ?? ""}
                          onChange={(next) => patchQuestion(index, { noneDescription: next })}
                          placeholder="None of the listed options fits."
                          readOnly={readOnly}
                        />
                      </Field>
                    </div>
                  )}
                </div>
              )}

              {q.type === "noul" && (
                <div className="grid grid-cols-1 gap-2">
                  <Field label='What counts as "yes" (optional)'>
                    <TextInput
                      value={q.trueDescription ?? ""}
                      onChange={(next) => patchQuestion(index, { trueDescription: next })}
                      placeholder="Mentions a prior ticket or says they have asked before"
                      readOnly={readOnly}
                    />
                  </Field>
                  <Field label='What counts as "no" (optional)'>
                    <TextInput
                      value={q.falseDescription ?? ""}
                      onChange={(next) => patchQuestion(index, { falseDescription: next })}
                      placeholder="No sign of any previous contact"
                      readOnly={readOnly}
                    />
                  </Field>
                </div>
              )}

              {q.type === "score" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
                      Levels, lowest first (each is a branch)
                    </p>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => levels.length < 10 && patchQuestion(index, { levels: [...levels, ""] })}
                        className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-accent transition-colors hover:bg-accent/10"
                      >
                        <Plus size={12} />
                        Add level
                      </button>
                    )}
                  </div>
                  {(levels.length >= 2 ? levels : [...levels, "", ""].slice(0, 2)).map((l, li) => (
                    <div key={li} className="flex items-center gap-2">
                      <span className="w-5 shrink-0 text-[11px] font-mono text-text-muted">{li}</span>
                      <div className="min-w-0 flex-1">
                        <TextInput
                          value={l}
                          onChange={(next) => {
                            const base = levels.length >= 2 ? [...levels] : [...levels, "", ""].slice(0, 2);
                            base[li] = next;
                            patchQuestion(index, { levels: base });
                          }}
                          placeholder="A concrete situation, not a degree"
                          readOnly={readOnly}
                        />
                      </div>
                      {!readOnly && levels.length > 2 && (
                        <button
                          type="button"
                          onClick={() => patchQuestion(index, { levels: levels.filter((_l, i) => i !== li) })}
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
                          aria-label="Remove level"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <p className="text-[11px] text-text-muted">
          Questions in one node are answered together and cannot see each other. A
          question that depends on another answer belongs in a second Decision node.
        </p>
      </section>

      {/* ── Routing ───────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
          Routing
        </p>
        <Field label="Branch on">
          <Select
            value={routedId}
            onChange={(next) => setRoute({ question: next })}
            options={questions.map((q) => ({ value: q.id, label: `${q.id} (${QUESTION_TYPE_LABELS[q.type] ?? q.type})` }))}
            disabled={readOnly || questions.length === 0}
          />
        </Field>
        {routed?.type === "noul" ? (
          <div className="grid grid-cols-2 gap-2">
            <Field label='"Yes" at or above'>
              <NumberInput
                value={typeof route.yesThreshold === "number" ? route.yesThreshold : undefined}
                onChange={(v) => setRoute({ yesThreshold: v })}
                placeholder="0.5"
                readOnly={readOnly}
                min={0}
                max={1}
                step={0.05}
              />
            </Field>
            <Field label='"No" at or below'>
              <NumberInput
                value={typeof route.noThreshold === "number" ? route.noThreshold : undefined}
                onChange={(v) => setRoute({ noThreshold: v })}
                placeholder="same as yes"
                readOnly={readOnly}
                min={0}
                max={1}
                step={0.05}
              />
            </Field>
          </div>
        ) : (
          <Field label="Minimum confidence (0–1)">
            <NumberInput
              value={typeof route.minConfidence === "number" ? route.minConfidence : undefined}
              onChange={(v) => setRoute({ minConfidence: v })}
              placeholder="0.85 for anything that closes, sends or deletes"
              readOnly={readOnly}
              min={0}
              max={1}
              step={0.05}
            />
          </Field>
        )}
        <Field label="Fallback branch (for none / unsure / unavailable)">
          <Select
            value={typeof route.fallbackBranch === "string" && valueHandles.includes(route.fallbackBranch) ? route.fallbackBranch : ""}
            onChange={(next) => setRoute({ fallbackBranch: next || undefined })}
            options={[{ value: "", label: "— none (those outcomes end the run unless connected) —" }, ...valueHandles.map((h) => ({ value: h, label: h }))]}
            disabled={readOnly || valueHandles.length === 0}
          />
        </Field>
        <p className="text-[11px] text-text-muted">
          Set the fallback to the safe path (usually the agent review) so an unsure
          answer or a provider outage never blocks a run. Confidence says how
          concentrated the answer was, not whether it was right: calibrate the
          threshold on real runs before trusting it for anything irreversible.
        </p>
      </section>

      {/* ── Advanced ──────────────────────────────────────────────────── */}
      <section className="grid grid-cols-2 gap-2">
        <Field label="Model">
          <TextInput
            value={typeof data.model === "string" ? data.model : ""}
            onChange={(next) => update({ model: next })}
            placeholder="jev-latest"
            readOnly={readOnly}
            mono
          />
        </Field>
        <Field label="Timeout (ms)">
          <NumberInput
            value={typeof data.timeoutMs === "number" ? data.timeoutMs : undefined}
            onChange={(v) => update({ timeoutMs: v })}
            placeholder="20000"
            readOnly={readOnly}
            min={1000}
            max={60000}
            step={1000}
          />
        </Field>
      </section>
    </div>
  );
}
